import React from "react";
import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { cn } from "@/lib/utils";

type Event = {
  sessions: number;
  messages: number;
  calls: number;
  feedbacks: number;
  upcoming: number;
};

type CalendarProps = {
  year: number;
  month: number;
  events: Record<string, Event>;
};

const EventDot: React.FC<{ type: keyof Event; count: number }> = ({
  type,
  count,
}) => {
  if (count === 0) return null;

  const colorMap: Record<keyof Event, string> = {
    sessions: "bg-red-500",
    messages: "bg-blue-500",
    calls: "bg-green-500",
    feedbacks: "bg-pink-500",
    upcoming: "bg-orange-500",
  };

  return <div className={`h-1.5 w-1.5 rounded-full ${colorMap[type]}`} />;
};

const CalendarDay: React.FC<{
  date: Date;
  events: Event | undefined;
  isCurrentMonth: boolean;
}> = ({ date, events, isCurrentMonth }) => {
  const dayClasses = cn(
    "flex flex-col items-center justify-center w-8 h-8 rounded-full",
    isCurrentMonth ? "text-gray-900" : "text-gray-400",
    isToday(date) && "bg-black text-white",
  );

  return (
    <div className="flex flex-col items-center">
      <div className={dayClasses}>{format(date, "d")}</div>
      {events && (
        <div className="mt-1 flex gap-0.5">
          {Object.entries(events).map(([type, count]) => (
            <EventDot key={type} type={type as keyof Event} count={count} />
          ))}
        </div>
      )}
    </div>
  );
};

const Legend: React.FC = () => {
  const legendItems = [
    { color: "bg-red-500", label: "Session" },
    { color: "bg-blue-500", label: "Text" },
    { color: "bg-green-500", label: "Video Call" },
    { color: "bg-pink-500", label: "Feedback" },
    { color: "bg-orange-500", label: "Upcoming" },
  ];

  return (
    <div className="mt-4 flex justify-center space-x-2">
      {legendItems.map((item) => (
        <div key={item.label} className="flex items-center space-x-1">
          <div className={`h-2 w-2 rounded-full ${item.color}`} />
          <span className="text-xs">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

export const TeenCalendar: React.FC<CalendarProps> = ({
  year,
  month,
  events,
}) => {
  const startDate = startOfMonth(new Date(year, month - 1));
  const endDate = endOfMonth(startDate);
  const startCalendar = startOfWeek(startDate);
  const endCalendar = endOfWeek(endDate);

  const days = eachDayOfInterval({ start: startCalendar, end: endCalendar });

  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];

  return (
    <div className="mx-auto w-full rounded-xl bg-white p-3">
      <div className="mb-2 grid grid-cols-7 gap-2">
        {weekDays.map((day, index) => (
          <div key={day + index} className="text-center font-semibold">
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const dateKey = format(day, "yyyy-MM-dd");
          return (
            <CalendarDay
              key={dateKey}
              date={day}
              events={events[dateKey]}
              isCurrentMonth={isSameMonth(day, startDate)}
            />
          );
        })}
      </div>
      <Legend />
    </div>
  );
};
